// src/layout/builders/ProjectLive/LiveTable.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import ProjectTable from "../ProjectTable";
import { toast } from "react-hot-toast";
import { UploadCloud, XCircle } from "lucide-react";
import SuccessPopup from "../../../components/common/SuccessPopup"; // Import SuccessPopup

const allowedStatusesForLive = ["hold", "rejected", "completion_request"];

const LiveTable = () => {
  const navigate = useNavigate();
  const {
    projects,
    isLoading,
    fetchProjects,
    updateProjectStatus,
    checkCompletionDocumentsStatus,
  } = useProjectStatus();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [missingDocsForModal, setMissingDocsForModal] = useState([]);
  const [modalProjectId, setModalProjectId] = useState(null);

  // --- Success Popup State ---
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  const [redirectPath, setRedirectPath] = useState(""); // Store path for redirection
  // --- End Success Popup State ---

  useEffect(() => {
    fetchProjects("active");
  }, [fetchProjects]);

  const handleStatusChange = async (projectId, newStatus, comment = null) => {
    if (newStatus === "completion_request") {
      const docStatusResult = await checkCompletionDocumentsStatus(projectId);
      if (!docStatusResult) {
        toast.error("Could not verify document status. Please try again.");
        return { success: false };
      }
      if (docStatusResult.allRequiredUploaded) {
        // Proceed with API call *after* confirmation (which happens in StatusChangePopup)
        const updateResult = await updateProjectStatus(projectId, newStatus);
        if (updateResult?.success) {
          // Show success popup and set redirect path
          setSuccessPopupMessage("Completion request submitted successfully!");
          setRedirectPath("/builder/completionrequest");
          setIsSuccessPopupOpen(true);
        }
        return updateResult;
      } else {
        // Show Missing Docs Modal (already handled this logic)
        const missing = docStatusResult.documents
          .filter((doc) => doc.required && !doc.uploaded)
          .map((doc) => doc.name);
        setMissingDocsForModal(missing);
        setModalProjectId(projectId);
        setShowUploadModal(true);
        return { success: false, reason: "missing_docs" };
      }
    } else {
      // Handle other statuses (hold, rejected) *after* confirmation (in StatusChangePopup)
      const result = await updateProjectStatus(projectId, newStatus, comment);
      if (result?.success) {
        // Show success popup and set redirect path
        if (newStatus === "rejected") {
          setSuccessPopupMessage(
            "Project status successfully updated to Rejected."
          );
          setRedirectPath("/builder/rejectionproject");
          setIsSuccessPopupOpen(true);
        } else if (newStatus === "hold") {
          // No success popup for hold, just navigate directly
          navigate("/builder/holdproject");
        } else {
          fetchProjects("active"); // Refresh for other statuses if needed
        }
      }
      return result;
    }
  };

  const handleNavigateToUpload = () => {
    // ... (remains the same)
    if (modalProjectId) {
      navigate(`/upload-doc/${modalProjectId}`);
      setShowUploadModal(false);
      setModalProjectId(null);
      setMissingDocsForModal([]);
    }
  };

  // --- Close Success Popup and Redirect ---
  const closeSuccessPopupAndRedirect = () => {
    setIsSuccessPopupOpen(false);
    if (redirectPath) {
      navigate(redirectPath);
      setRedirectPath(""); // Clear path after navigation
    }
  };

  if (isLoading && projects.length === 0) {
    // ... (loading spinner remains the same)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Success Popup */}
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={closeSuccessPopupAndRedirect}
        message={successPopupMessage}
      />

      {/* Upload Documents Required Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          {/* ... (Upload Modal content remains the same) ... */}
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center transform transition-all scale-100 opacity-100">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Upload Required Documents
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              To request project completion, please upload all required
              documents first.
            </p>
            {/* List of Missing Documents */}
            {missingDocsForModal.length > 0 && (
              <div className="text-left mb-4 bg-red-50 p-3 rounded-md border border-red-200 max-h-32 overflow-y-auto">
                <p className="text-xs font-medium text-red-700 mb-1">
                  Missing Documents:
                </p>
                <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                  {missingDocsForModal.map((docName, index) => (
                    <li key={index}>{docName}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Modal Action Buttons */}
            <div className="flex justify-center gap-4 mt-5">
              <button
                onClick={() => setShowUploadModal(false)} // Just close the modal
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleNavigateToUpload} // Navigate to upload page
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center"
              >
                <UploadCloud className="w-4 h-4 mr-1.5" />
                Upload Now
              </button>
            </div>
          </div>
        </div>
      )}

      <ProjectTable
        title="Live Projects"
        projects={projects}
        handleStatusChange={handleStatusChange} // Pass the updated handler
        navigate={navigate}
        allowedNextStatuses={allowedStatusesForLive}
        showActionsColumn={true}
      />
    </>
  );
};

export default LiveTable;
